import { t } from '@lingui/core/macro';
import { logToExternalErrorHandlers } from '@luminovo/commons';
import { CenteredLayout, CenteredSpinner, LockFilledIcon, NonIdealState } from '@luminovo/design-system';
import { type Query, useQuery, useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import React, { type JSX } from 'react';

import { useAuth } from '../hooks/useAuth';
import { getTokenIfPresent, isSameUser, setToken } from '../token';
import { TokenContext } from '../TokenContext';

interface AuthenticatedContextProviderBaseProps {
    /** Should redirect to login; repeats with `again: true` on later refusals, since a navigation can overtake it. */
    onNotAuthenticated: (options: { redirectTo: string; again: boolean }) => void;

    /** Should reload, or the pages, unsaved forms included, would act for another user who signed in in another tab. */
    onUserChanged: () => void;

    /** Called for every token request made while the pages hold a token that doesn't hand that token back. */
    onTokenRenewal?: (renewal: TokenRenewal) => void;

    /**
     * The organization ID to use for the authentication.
     */
    auth0OrgId: string | undefined;
}

/** A token request made while the pages held a token; a cache hit that hands back the held token isn't one. */
export interface TokenRenewal {
    outcome: 'renewed' | 'user_changed' | 'failed';
    /** Auth0's error code (`login_required`, `timeout`, …) for a failed request. */
    error?: string;
    heldTokenAgeSeconds?: number;
    /** By the client's clock. */
    heldTokenExpired?: boolean;
}

type AuthenticatedContextProviderProps = React.PropsWithChildren<AuthenticatedContextProviderBaseProps>;

export const ACCESS_TOKEN_QUERY_KEY = ['access-token'];

const LOST_SESSION_RECHECK_MILLIS = 5_000;

export function AuthenticatedContextProvider({
    children,
    onNotAuthenticated,
    onUserChanged,
    onTokenRenewal,
    auth0OrgId,
}: AuthenticatedContextProviderProps): JSX.Element {
    const { logout } = useAuth({ auth0OrgId });
    const { error, isPending, isAuthenticated, token } = useFetchTokenSilently({
        onNotAuthenticated,
        onUserChanged,
        onTokenRenewal,
        auth0OrgId,
    });

    // Renewing must not land here: swapping the pages for a spinner unmounts them, losing unsaved work.
    if (isPending || token === null) {
        return <CenteredSpinner />;
    }

    // Handle auth0 errors
    if (error || !isAuthenticated) {
        if (error) {
            logToExternalErrorHandlers(error ?? new Error('Authentication error'));
        }

        return (
            <CenteredLayout>
                <NonIdealState
                    variant="error"
                    icon={<LockFilledIcon />}
                    title={t`Authentication problem`}
                    description={
                        t`Sorry, it looks like your session expired. Try refreshing the page or logging in again. If this problem persists, please reach out to our support team using the chat box in the bottom right corner.` +
                        `\n\n${error?.message}`
                    }
                    action={{
                        children: t`Log out`,
                        onClick: () => {
                            void logout();
                        },
                    }}
                />
            </CenteredLayout>
        );
    }

    return (
        <React.Suspense fallback={<CenteredSpinner />}>
            <TokenContext.Provider value={token}>{children}</TokenContext.Provider>
        </React.Suspense>
    );
}

function useFetchTokenSilently({
    onNotAuthenticated,
    onUserChanged,
    onTokenRenewal,
    auth0OrgId,
}: {
    onNotAuthenticated: AuthenticatedContextProviderBaseProps['onNotAuthenticated'];
    onUserChanged: AuthenticatedContextProviderBaseProps['onUserChanged'];
    onTokenRenewal: AuthenticatedContextProviderBaseProps['onTokenRenewal'];
    auth0OrgId: string | undefined;
}): {
    isAuthenticated: boolean;
    /** No token yet: Auth0 is loading, or the first request for one hasn't settled. A query key change starts over. */
    isPending: boolean;
    error: Error | undefined | null;
    /** `null` once the session is lost. */
    token: string | null | undefined;
} {
    const { isAuthenticated, isLoading, error, getAccessTokenSilently } = useAuth({ auth0OrgId });
    const queryClient = useQueryClient();
    // Read at the first refusal only: by a later one, the address bar may already show the login page.
    const lostSessionPage = React.useRef<string | undefined>(undefined);

    // refresh every hour
    const tokenRefreshIntervalMillis = 1000 * 60 * 60;
    // Handle token refresh
    const {
        data: token,
        error: tokenError,
        isPending: isTokenPending,
    } = useQuery({
        enabled: !isLoading,
        queryKey: [...ACCESS_TOKEN_QUERY_KEY, isAuthenticated, isLoading],
        refetchOnWindowFocus: true,
        queryFn: async ({ queryKey }) => {
            // The token the pages hold: this query's data. The token module outlives a lost session and the layout.
            const heldToken = queryClient.getQueryData<string | null>(queryKey);
            try {
                const token = await getAccessTokenSilently();
                const previousToken = getTokenIfPresent();
                if (previousToken !== undefined && previousToken !== token) {
                    const sameUser = isSameUser(previousToken, token);
                    if (typeof heldToken === 'string') {
                        onTokenRenewal?.({
                            outcome: sameUser ? 'renewed' : 'user_changed',
                            ...describeHeldToken(heldToken),
                        });
                    }
                    if (!sameUser) {
                        onUserChanged();
                        // Until the reload: the previous user's token, or nothing once the session was lost.
                        return heldToken ?? null;
                    }
                }
                lostSessionPage.current = undefined;
                setToken(token);
                if (previousToken !== undefined && previousToken !== token) {
                    // The 401 handler renews only after a request's last retry, so nothing else would resend it.
                    void queryClient.refetchQueries({ type: 'active', predicate: failedWithStaleToken });
                }
                return token;
            } catch (err) {
                // Once per lost session: the requests that follow it are made without a token.
                if (typeof heldToken === 'string') {
                    onTokenRenewal?.({
                        outcome: 'failed',
                        error: auth0ErrorCode(err),
                        ...describeHeldToken(heldToken),
                    });
                }
                if (requiresSignIn(err)) {
                    const again = lostSessionPage.current !== undefined;
                    lostSessionPage.current ??= window.location.pathname + window.location.search;
                    onNotAuthenticated({ redirectTo: lostSessionPage.current, again });
                    return null;
                }
                throw err;
            }
        },
        throwOnError: false,
        // Rechecking every few seconds sends the user to login again if another navigation overtook the redirect.
        refetchInterval: (query) =>
            query.state.data === null ? LOST_SESSION_RECHECK_MILLIS : tokenRefreshIntervalMillis,
        refetchIntervalInBackground: true,
        retry: false,
        staleTime: 0,
    });

    // Nothing on screen reports a failed renewal; logged from state, since an invalidation can cancel one in flight.
    React.useEffect(() => {
        if (token && tokenError) {
            logToExternalErrorHandlers(tokenError);
        }
    }, [token, tokenError]);

    return {
        isAuthenticated,
        isPending: isLoading || isTokenPending,
        // Not an error while the pages have a token: they keep it, and if it has run out, their 401s renew it.
        error: error || (token ? null : tokenError),
        token,
    };
}

/** Refusals until the user signs in again; an iframe timeout or network failure says nothing about the session. */
const SIGN_IN_REQUIRED_ERRORS: ReadonlySet<string> = new Set([
    'login_required',
    'consent_required',
    'interaction_required',
    'mfa_required',
    // Auth0 turns the user away, e.g. blocked or denied by a rule or action.
    'unauthorized',
    'access_denied',
    'invalid_grant',
]);

/** A 401 with code `unknown`, which a new token may fix; one that names its reason refuses a valid token. */
function failedWithStaleToken(query: Query): boolean {
    const error: unknown = query.state.error;
    // Read by name: this package doesn't depend on the HTTP client's `HttpError`.
    return (
        error instanceof Error &&
        'status' in error &&
        error.status === 401 &&
        'code' in error &&
        error.code === 'unknown'
    );
}

/** Auth0's errors carry the OAuth error code in `error`, and its description in `message`. */
function requiresSignIn(err: unknown): boolean {
    return err instanceof Error && 'error' in err && SIGN_IN_REQUIRED_ERRORS.has(String(err.error));
}

/** Auth0's error code, or the error's name for anything else. */
function auth0ErrorCode(err: unknown): string {
    if (err instanceof Error) {
        return 'error' in err ? String(err.error) : err.name;
    }
    return 'unknown';
}

/** How old the held token is and whether it has expired, from its claims; nothing for a token that won't decode. */
function describeHeldToken(token: string): Pick<TokenRenewal, 'heldTokenAgeSeconds' | 'heldTokenExpired'> {
    try {
        const { iat, exp } = jwtDecode(token);
        const nowSeconds = Date.now() / 1000;
        return {
            heldTokenAgeSeconds: iat === undefined ? undefined : Math.round(nowSeconds - iat),
            heldTokenExpired: exp === undefined ? undefined : exp < nowSeconds,
        };
    } catch {
        return {};
    }
}
